// Contact page component - Backend email integration
// NO EmailJS - Uses Node.js backend with Nodemailer

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt,
  FaUser,
  FaGraduationCap,
  FaUserTie
} from 'react-icons/fa';
import './Contact.css';

// NO EmailJS initialization - removed completely

function Contact() {
  const navigate = useNavigate();
  const contactRef = React.useRef(null);
  const isInView = useInView(contactRef, { once: true });

  const [formData, setFormData] = useState({
    studentName: '',
    studentEmail: '',
    studentPhone: '',
    studentClass: '',
    subjects: '',
    preferredCountry: '',
    graduationYear: '',
    parentName: '',
    parentPhone: '',
    servicesRequired: '',
    address: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  // Function to send form data to backend
  const sendFormData = async (submissionData) => {
    try {
      console.log('Sending form data to backend...');
      
      // Send POST request to backend API
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const data = await response.json();
      console.log('Backend response:', data);

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit form');
      }

      return { success: true, message: data.message };
    } catch (error) {
      console.error('Form submission error:', error);
      
      // Check if backend is running
      if (error.message.includes('Failed to fetch') || error.message.includes('Network')) {
        return { 
          success: false, 
          message: '⚠️ Cannot connect to server. Please make sure the backend is running (npm run server)' 
        };
      }
      
      return { 
        success: false, 
        message: error.message || 'Failed to submit form. Please try again.' 
      };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setIsSubmitting(true);

    try {
      // Send form data to backend
      const result = await sendFormData(formData);

      if (result.success) {
        // Save to localStorage for admin dashboard
        saveSubmissionToStorage(formData);
        
        setSubmitSuccess(true);
        setIsSubmitting(false);

        // Redirect to success page after delay
        setTimeout(() => {
          navigate('/contact-success');
        }, 2000);
      } else {
        setSubmitError(result.message);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError('An unexpected error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  const saveSubmissionToStorage = (submissionData) => {
    const existingSubmissions = localStorage.getItem('contactSubmissions');
    let submissions = [];
    
    if (existingSubmissions) {
      submissions = JSON.parse(existingSubmissions);
    }
    
    const newSubmission = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      ...submissionData
    };
    
    submissions.unshift(newSubmission);
    localStorage.setItem('contactSubmissions', JSON.stringify(submissions));
  };

  const contactInfo = [
    {
      icon: <FaPhone style={{ transform: "rotate(90deg)" }} />,
      title: 'Phone',
      value: '+971 55 272 8339',
      description: 'Call us during business hours'
    },
    {
      icon: <FaEnvelope />,
      title: 'Email',
      value: 'divya@engagehub.me',
      description: 'Send us an email anytime'
    }
  ];

  return (
    <div className="contact-page">
      <section className="contact-header">
        <div className="container">
          <motion.div 
            className="contact-header-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="contact-title1">Get in Touch</h1>
            <p className="contact-subtitle1">
              Ready to start your academic journey? Contact us today and let's discuss 
              how we can help you achieve your educational goals.
            </p>
          </motion.div>
        </div>
      </section>

      <section ref={contactRef} className="contact-main">
        <div className="container">
          <div className="contact-content">
            <motion.div 
              className="contact-info-section"
              initial={{ opacity: 0, x: -50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <h2 className="section-title">Contact Information</h2>
              <p className="section-subtitle">
                We're here to help you succeed. Reach out to us through any of these channels.
              </p>
              
              <div className="contact-info-grid">
                {contactInfo.map((item, index) => (
                  <motion.div 
                    key={item.title}
                    className="contact-info-item"
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <div className="contact-icon">
                      {item.icon}
                    </div>
                    <div className="contact-details">
                      <h3 className="contact-item-title">{item.title}</h3>
                      <p className="contact-item-value">{item.value}</p>
                      <p className="contact-item-description">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div 
              className="contact-form-section"
              initial={{ opacity: 0, x: 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="section-title">Send us a Message</h2>
              <p className="section-subtitle">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="studentName" className="form-label">
                      <FaUser className="field-icon" />
                      Student Name *
                    </label>
                    <input
                      type="text"
                      id="studentName"
                      name="studentName"
                      value={formData.studentName}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      placeholder="Enter student's full name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="studentEmail" className="form-label">
                      <FaEnvelope className="field-icon" />
                      Student Email *
                    </label>
                    <input
                      type="email"
                      id="studentEmail"
                      name="studentEmail"
                      value={formData.studentEmail}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      placeholder="Enter student's email address"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="studentPhone" className="form-label">
                      <FaPhone className="contact-icon-phone" />
                      Student Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="studentPhone"
                      name="studentPhone"
                      value={formData.studentPhone}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      placeholder="Enter student's phone number"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="studentClass" className="form-label">
                      <FaGraduationCap className="field-icon" />
                      Current Class/Grade *
                    </label>
                    <input
                      type="text"
                      id="studentClass"
                      name="studentClass"
                      value={formData.studentClass}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      placeholder="e.g. 10 / High School / College Year 2"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="subjects" className="form-label">
                      <FaGraduationCap className="field-icon" />
                      Subjects of Interest *
                    </label>
                    <input
                      type="text"
                      id="subjects"
                      name="subjects"
                      value={formData.subjects}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      placeholder="e.g. Mathematics, Computer Science, Economics"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="preferredCountry" className="form-label">
                      <FaMapMarkerAlt className="field-icon" />
                      Preferred Country
                    </label>
                    <input
                      type="text"
                      id="preferredCountry"
                      name="preferredCountry"
                      value={formData.preferredCountry}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="e.g. USA, UK, Canada, Germany"
                    />
                  </div>
                </div>

                
                 <div className="form-group">
  <label htmlFor="servicesRequired" className="form-label">
    <FaUserTie className="field-icon" />
    Services Required *
  </label>
  <select
    id="servicesRequired"
    name="servicesRequired"
    value={formData.servicesRequired}
    onChange={handleInputChange}
    className="form-input"
    required
  >
    <option value="" disabled>Select a service</option>
    <option value="University Admissions Counseling">SAT</option>
    <option value="Career Guidance">ACT</option>
    <option value="Visa Assistance">GMAT</option>
    <option value="Scholarship Guidance">GRE</option>
    <option value="Test Preparation (SAT/IELTS/TOEFL)">AP COURCES</option>
    <option value="Application Essay Review">MYP</option>
    <option value="Application Essay Review">AMC</option>
    <option value="Application Essay Review">IGCSE</option>
    <option value="Application Essay Review">A LEVEL</option>
    <option value="Application Essay Review">IB</option>
    <option value="Application Essay Review">Admission Counselling</option>
    <option value="Application Essay Review">Research Assistance</option>
    <option value="Application Essay Review">Essay Editing Services</option>
    <option value="Other">Other</option>
  </select>
</div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="parentName" className="form-label">
                      <FaUserTie className="field-icon" />
                      Parent's Name *
                    </label>
                    <input
                      type="text"
                      id="parentName"
                      name="parentName"
                      value={formData.parentName}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      placeholder="Enter parent's full name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="parentPhone" className="form-label">
                      <FaPhone className="contact-icon-phone" />
                      Parent's Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="parentPhone"
                      name="parentPhone"
                      value={formData.parentPhone}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      placeholder="Enter parent's phone number"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="graduationYear" className="form-label">
                      <FaGraduationCap className="field-icon" />
                      Year of Graduation
                    </label>
                    <input
                      type="number"
                      id="graduationYear"
                      name="graduationYear"
                      value={formData.graduationYear}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="e.g. 2026"
                      min="1900"
                      max="2100"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="address" className="form-label">
                      <FaMapMarkerAlt className="field-icon" />
                      Address *
                    </label>
                    <textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      placeholder="Enter your full address"
                      rows="3"
                    ></textarea>
                  </div>
                </div>

                {submitError && (
                  <motion.div 
                    className="error-message"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <p>{submitError}</p>
                    {submitError.includes('backend') && (
                      <p style={{ fontSize: '14px', marginTop: '10px' }}>
                        Run this command in a separate terminal:<br/>
                        <code style={{ background: '#f4f4f4', padding: '5px 10px', borderRadius: '4px', display: 'inline-block', marginTop: '5px' }}>
                          npm run server
                        </code>
                      </p>
                    )}
                  </motion.div>
                )}

                <motion.button
                  type="submit"
                  className={`submit-btn ${isSubmitting ? 'submitting' : ''}`}
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </motion.button>

                {submitSuccess && (
                  <motion.div 
                    className="success-message"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <p>✅ Thank you! Your message has been sent successfully.</p>
                    <p>📧 You will receive a confirmation email from Divya shortly.</p>
                    <p>⏰ We'll get back to you within 24 hours.</p>
                  </motion.div>
                )}
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;